import React from "react";
import _ from "lodash";
import { Paper, makeStyles, lighten } from "@material-ui/core";
import * as FIBER from "@react-three/fiber";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import Sphere from "./sphere-component";
import { useSelector, Provider, useDispatch } from "react-redux";
import { store } from "./../index";
import { borderRadiusValue } from "../lib/constants";
import Hud from "./Hud";
import { setCameraRotation, setCameraZ } from "../epics/cameraEpic";
import Connector from "./Connector";
import TaxonomyData from "../taxonomyData.json";
import { colors } from "../lib/constants";

const useStyles = makeStyles({
  container: {
    width: "100%",
    height: "100%",
    cursor: "pointer",
    backgroundColor: "transparent",
    borderRadius: borderRadiusValue
  }
});

function CameraControls() {
  const dispatch = useDispatch();
  const cameraZ = useSelector((state) => state.cameraZ);
  const { camera } = FIBER.useThree();
  const [rotation, setRotation] = React.useState([]);
  const [z, setZ] = React.useState(0);
  React.useEffect(() => {
    camera.zoom = cameraZ;
    camera.updateProjectionMatrix();
  }, [cameraZ, camera]);
  FIBER.useFrame(() => {
    const cameraRotation = _.map(camera.rotation.toVector3().toArray(), (num) => num.toFixed(4));
    if (!_.isEqual(rotation, cameraRotation)) {
      dispatch(setCameraRotation(cameraRotation));
      setRotation(cameraRotation);
    }
    if (z !== camera.zoom) {
      setZ(camera.zoom);
      dispatch(setCameraZ(camera.zoom));
    }
  });
  return null;
}

export default function CanvasComp() {
  const classes = useStyles();
  const checkedCardStepOne = useSelector((state) => state.checkedCardStepOne, _.isEqual);
  const checkedCardStepTwo = useSelector((state) => state.checkedCardStepTwo, _.isEqual);
  const taxonomyTagsAndPositions = useSelector((state) => state.taxonomyTagsAndPositions, _.isEqual);
  const groups = useSelector((state) => state.group, _.isEqual);
  const taxonomyGroups = useSelector((state) => state.taxonomyGroups, _.isEqual);
  const checkedTags = useSelector((state) => state.checkedTags, _.isEqual);
  const taxaConnectors = useSelector((state) => state.connectors, _.isEqual);
  const taxa = _.union(..._.map(TaxonomyData, (data, key) => _.map(data, (taxon) => ({ taxon: taxon, group: key }))));
  const tagList = React.useMemo(
    () => checkedTags?.[checkedCardStepOne + " " + checkedCardStepTwo],
    [checkedTags, checkedCardStepOne, checkedCardStepTwo]
  );
  const keyphrasesAndPositions = React.useMemo(
    () => taxonomyTagsAndPositions?.[checkedCardStepOne + " " + checkedCardStepTwo],
    [taxonomyTagsAndPositions, checkedCardStepOne, checkedCardStepTwo]
  );
  const connectors = React.useMemo(
    () => taxaConnectors?.[checkedCardStepOne + " " + checkedCardStepTwo],
    [taxaConnectors, checkedCardStepOne, checkedCardStepTwo]
  );

  return (
    <Paper className={classes.container}>
      <Canvas
        gl={{ alpha: true, antialias: true }}
        camera={{ fov: 55, position: [30, 30, 30] }}
        shadowMap
        style={{ borderRadius: borderRadiusValue }}
      >
        <Provider store={store}>
          {_.map(tagList, (tag) => {
            const suggestedColorKey = _.findKey(
              taxonomyGroups[checkedCardStepOne + " " + checkedCardStepTwo],
              (taxonomyGroup) => _.some(taxonomyGroup, (keyphrase) => keyphrase.node === tag)
            );
            const colorKey = _.findKey(groups[checkedCardStepOne + " " + checkedCardStepTwo], (group) =>
              _.some(group, (entry) => entry.name === tag)
            );
            const color = colorKey === undefined ? lighten(colors[suggestedColorKey], 0.375) : colors[colorKey];
            return (
              <Sphere
                keyphrase={tag}
                position={keyphrasesAndPositions?.[0][_.indexOf(keyphrasesAndPositions?.[1], tag)]}
                color={color}
                key={`sphere-${tag}`}
              />
            );
          })}
          {_.map(connectors, (connectorGroup, cgIndex) =>
            _.map(connectorGroup, (connector, cIndex) => (
              <Connector
                start={connector.start}
                end={connector.end}
                key={`connector-${cgIndex}-${cIndex}-${performance.now()}`}
              />
            ))
          )}
          {_.map(taxa, (taxon) => (
            <Sphere
              keyphrase={taxon.taxon}
              position={keyphrasesAndPositions?.[0][_.indexOf(keyphrasesAndPositions[1], taxon.taxon)]}
              groupName={taxon.group}
              color={colors[taxon.group]}
              taxon={true}
              key={`sphere-${taxon.taxon}`}
            />
          ))}
          <CameraControls />
        </Provider>
        <OrbitControls />
        <ambientLight intensity={0.5} />
        <pointLight position={[100, 100, 100]} />
      </Canvas>
      <Hud />
    </Paper>
  );
}
